import { useState } from 'react'

import { Modal, useModals } from 'almanac-ui/Modal'
import { AlmanacFileCalendarEvent } from 'screens/Calendar/types'

import Styles from './DuplicateModal.module.scss'
import { Text } from 'blaze-ui/Text/Text'
import { CalendarEventCard } from '../CalendarEventCard'
import { ScheduleDropdown } from './ScheduleDropdown/ScheduleDropdown'
import { IntegrationsDropdown } from './IntegrationsDropdown/IntegrationsDropdown'
import { CampaignsDropdown } from './CampaignsDropdown/CampaignsDropdown'
import { useDuplicateScheduledPost } from 'hooks/queries/scheduledPosts/useDuplicateScheduledPost'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'

export const DuplicateModal = ({
  event,
  campaignId: initialCampaignId,
}: {
  event: AlmanacFileCalendarEvent
  campaignId?: number
}) => {
  const { closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const [postAt, setPostAt] = useState(event.post_at)
  const [postTo, setPostTo] = useState<number[]>(
    event.scheduled_post_targets?.map((target) => target.external_integration_id) ?? [],
  )
  const [campaignId, setCampaignId] = useState<number | undefined>(initialCampaignId)
  const { mutate: duplicateScheduledPost, isLoading: isProcessing } = useDuplicateScheduledPost({
    onSuccess: closeModal,
  })

  // scheduled_post_id is only populated for scheduled_posts_v2 calendar items; fall back to the
  // nested post / a target so the duplicate still works, and surface a clear error otherwise
  // instead of silently doing nothing.
  const scheduledPostId =
    event.scheduled_post_id ?? event.scheduled_post?.id ?? event.scheduled_post_targets?.[0]?.scheduled_post_id

  const duplicatePost = () => {
    if (!scheduledPostId) {
      showToastMessage('This post can’t be duplicated — no scheduled post was found for it.', 'error')
      return
    }

    duplicateScheduledPost({
      workspaceId,
      scheduledPostId,
      params: { postAt, postTo, campaignId },
    })
  }

  return (
    <Modal.Root bodyClassName={Styles.modalBody}>
      <Modal.Header title="Duplicate this post" />
      <Modal.Content>
        <div className={Styles.content}>
          <CalendarEventCard
            event={event}
            cardWidth={150}
            cardHeight={254}
            loadingColor="var(--dark-10)"
            showHoverOverlay={false}
            showStatus={false}
          />
          <div className={Styles.settings}>
            <div className={Styles.setting}>
              <Text variant="secondary" color="var(--dark-60)">
                Posting on
              </Text>
              <ScheduleDropdown postAt={postAt} setPostAt={setPostAt} />
            </div>
            <div className={Styles.setting}>
              <Text variant="secondary" color="var(--dark-60)">
                Posting to
              </Text>
              <IntegrationsDropdown postTo={postTo} setPostTo={setPostTo} />
            </div>
            <div className={Styles.setting}>
              <Text variant="secondary" color="var(--dark-60)">
                Campaign
              </Text>
              <CampaignsDropdown setCampaignId={setCampaignId} selectedCampaignId={campaignId} />
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton onPress={closeModal} variant="secondary">
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterButton onPress={duplicatePost} isDisabled={isProcessing}>
          Duplicate Post
        </Modal.FooterButton>
      </Modal.Footer>
    </Modal.Root>
  )
}
